import { AUTH_CONTAINER_CLASS, AuthPage } from "@/components/auth";
import { Button, useToast } from "@/components/ui";
import { useStore } from "@/store";
import { performAuthFailureLogout } from "@/utils/authUtils";
import { authService } from "@/api/auth";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Mail, MessageCircle } from "lucide-react";
import { useCallback } from "react";

export type TVerifyMethod = "email" | "phone";

export const VerifyMethod = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const profile = useStore((s) => s.profile);
  const isSending = useStore((s) => s.isSendingVerificationOtp);
  const sendVerificationOtp = useStore((s) => s.sendVerificationOtp);

  const handleLogout = useCallback(async () => {
    await authService.logout();
    performAuthFailureLogout();
  }, []);

  const handleSelect = useCallback(
    async (method: TVerifyMethod) => {
      if (method === "phone") {
        const phoneNumber = profile?.phone_number?.trim();
        if (!phoneNumber) {
          toast({
            variant: "destructive",
            title: "Phone number required",
            description: "Please add a phone number to verify via WhatsApp.",
          });
          void navigate({ to: "/auth/edit-number" });
          return;
        }
      }

      const result = await sendVerificationOtp(method);
      if (!result.success) {
        toast({ variant: "destructive", title: result.error ?? "Failed to send code" });
        return;
      }

      toast({
        variant: "success",
        title: result.message ?? "Verification code sent",
      });

      void navigate({
        to: "/auth/verify-otp",
        search: {
          mode: method,
          // IMPORTANT: do not pass `phone` here; presence of `phone` indicates the \"edit number\" flow.
          phone: undefined,
          email: undefined,
          from: undefined,
        },
      });
    },
    [navigate, profile?.phone_number, sendVerificationOtp, toast]
  );

  return (
    <div className={AUTH_CONTAINER_CLASS}>
      <div className="grid gap-6">
        <AuthPage
          title="Verify your account"
          subtitle="Choose a method to receive a 6-digit verification code."
        >
          <div className="grid gap-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Button
                type="button"
                className="h-12 justify-start gap-3 px-5"
                onClick={() => void handleSelect("email")}
                disabled={isSending}
              >
                {isSending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
                <span className="font-semibold">Verify with Email</span>
              </Button>

              <Button
                type="button"
                variant="outline"
                className="h-12 justify-start gap-3 px-5"
                onClick={() => void handleSelect("phone")}
                disabled={isSending}
              >
                {isSending ? <Loader2 className="h-5 w-5 animate-spin" /> : <MessageCircle className="h-5 w-5" />}
                <span className="font-semibold">Verify with WhatsApp</span>
              </Button>
            </div>

            <p className="pt-2 text-xs text-muted-foreground">Tip: you only need to verify one method to continue.</p>

            <button
              type="button"
              onClick={() => void handleLogout()}
              className="pt-2 text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              Log out
            </button>
          </div>
        </AuthPage>
      </div>
    </div>
  );
};
